/**
 * Anti-trigger guards for the achievement subsystem. Built so the
 * unlock flow never fires during a fresh install, never double-counts a
 * single-day fluke as a streak, and always respects the user's master
 * toggle.
 */

import type { Profile } from '../../data/types';

const COOLDOWN_MS = 24 * 60 * 60 * 1000;
const ENGAGEMENT_MIN_TX = 3;

/**
 * Should the unlock pipeline run at all? False during the cold-start
 * cooldown, when the user has barely engaged, when a global dev flag
 * is set, or when the user disabled the feature in Settings.
 */
export function canTriggerAchievements(
  profile: Profile,
  transactionCount: number,
  now: Date = new Date(),
): boolean {
  const a = profile.achievements;
  if (!a) return false;
  if (!a.achievementsEnabled) return false;

  const installedAt = Date.parse(a.installedAt);
  if (Number.isFinite(installedAt) && now.getTime() - installedAt < COOLDOWN_MS) {
    return false;
  }

  if (transactionCount < ENGAGEMENT_MIN_TX) return false;

  if (
    typeof globalThis !== 'undefined' &&
    (globalThis as { MARGIN_DEV_MODE?: boolean }).MARGIN_DEV_MODE === true
  ) {
    return false;
  }

  return true;
}

export type StreakDecision = 'increment' | 'reset' | 'same-day';

/**
 * Decide how a streak counter should react when the user's daily
 * action fires. `today` and `lastDate` are ISO YYYY-MM-DD strings.
 */
export function shouldRecordStreak(
  today: string,
  lastDate: string | null,
): StreakDecision {
  if (!lastDate) return 'increment';
  if (today === lastDate) return 'same-day';
  // diff in days; same-day handled above so any non-equal compare is
  // either +1 (increment) or > 1 (reset). Date math via UTC ms keeps
  // DST shifts from confusing the result.
  const a = isoToUtc(lastDate);
  const b = isoToUtc(today);
  const days = Math.round((b - a) / (24 * 60 * 60 * 1000));
  if (days === 1) return 'increment';
  return 'reset';
}

/**
 * Streaks below `requiredMinimum` shouldn't unlock anything. Filters
 * one-day flukes out of the achievement firing logic.
 */
export function isLegitimateStreak(
  streak: number,
  requiredMinimum: number,
): boolean {
  return streak >= requiredMinimum;
}

function isoToUtc(iso: string): number {
  const [y, m, d] = iso.split('-').map((s) => Number(s));
  return Date.UTC(y!, (m ?? 1) - 1, d ?? 1);
}
